import { type ReactNode, useState } from 'react';
import type { Creator } from '@/types';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useForceValidate, AiAppealAcceptForm } from '@/modules/admin';

type Force = ReturnType<typeof useForceValidate>;

// Dos salidas para un lead en limbo: "Validar" (tal cual) o "Corregir
// categoría y validar" (abre un form para fijar tipo/categoría/sexo y
// valida en el mismo PATCH, sin sancionar al collector).
function ActionButtons(p: {
  onValidate: () => void;
  onFix: () => void;
  disabled: boolean;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-center gap-sm mt-base">
      <Button variant="primary" size="sm" onClick={p.onValidate} disabled={p.disabled}>
        Validar
      </Button>
      <Button variant="secondary" size="sm" onClick={p.onFix} disabled={p.disabled}>
        Corregir categoría y validar
      </Button>
    </div>
  );
}

function FixForm(p: { creator: Creator; force: Force; onCancel: () => void }): ReactNode {
  return (
    <div className="mt-base">
      <AiAppealAcceptForm
        creator={p.creator}
        isPending={p.force.isPending}
        confirmLabel="Corregir y validar"
        onCancel={p.onCancel}
        onConfirm={(extraData) => p.force.mutate({ id: p.creator.id, extraData })}
      />
    </div>
  );
}

// Admin-only actions for leads rejected by AI without a pending appeal (the
// "limbo" case). Pending appeals are handled in /admin/ai/appeals where the
// admin gets the collector's appeal description for context.
export function CreatorAiAdminActions({ creator }: { creator: Creator }): ReactNode {
  const { isAdmin } = useAuth();
  const force = useForceValidate();
  const [fixing, setFixing] = useState(false);
  const isRejected = creator.ai_analysis_status === 'rejected_by_ai';
  const noAppeal = !creator.appeal_status || creator.appeal_status === 'none';
  if (!isAdmin || !isRejected || !noAppeal) return null;
  if (fixing) {
    return <FixForm creator={creator} force={force} onCancel={() => setFixing(false)} />;
  }
  return (
    <ActionButtons
      onValidate={() => force.mutate({ id: creator.id })}
      onFix={() => setFixing(true)}
      disabled={force.isPending}
    />
  );
}
